import React from "react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import type { RadioGroupProps } from "@radix-ui/react-radio-group";
import styles from "./styles.module.css";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import Stack from "@/components/atoms/Stack";
import IconTooltip from "../IconTooltip";

interface Option {
  value: string;
  label: string;
}

interface Props extends RadioGroupProps {
  title?: string;
  name: string;
  options: Option[];
  inlineTitle?: boolean;
  wrap?: boolean;
  justify?: "right";
  titleTooltipText?: string;
  testId?: string;
}

const RadioButtonGroup = (props: Props) =>
  props.title ? (
    <RadioButtonGroupWithTitle {...props} />
  ) : (
    <RadioGroup.Root
      className={styles.root}
      defaultValue={props.defaultValue}
      value={props.value}
      onValueChange={props.onValueChange}
      data-testid={"radio-button-group"}
      disabled={props.disabled}
      style={{
        flexWrap: props.wrap ? "wrap" : "nowrap",
        justifyContent: props.justify === "right" ? "flex-end" : "flex-start",
      }}
    >
      {props.options.map((option, index) => (
        <div
          data-testid={props.testId + `-${index}-wrapper`}
          style={{ display: "flex", alignItems: "center" }}
          key={index}
        >
          <RadioGroup.Item
            data-testid={props.testId + `-${index}-item`}
            className={styles.item}
            value={option.value}
            id={`${props.name}-${index}`}
          >
            <RadioGroup.Indicator data-testid={props.testId + `-${index}-indicator`} className={styles.indicator} />
          </RadioGroup.Item>
          <label
            data-testid={props.testId + `-${index}-label`}
            className={styles.label}
            htmlFor={`${props.name}-${index}`}
          >
            {option.label}
          </label>
        </div>
      ))}
    </RadioGroup.Root>
  );

const RadioButtonGroupWithTitle = ({ title, titleTooltipText, ...props }: Props) => {
  const Title = (
    <>
      <Text size="sm" color="secondary">
        <span style={{ position: "relative", display: "inline-block" }}>
          {title}
          {titleTooltipText && (
            <span style={{ position: "absolute", top: 0, right: "-20px" }}>
              <IconTooltip
                icon="info"
                label={titleTooltipText}
                width={12}
                height={12}
                color="var(--color-grey-75)"
                multiline
                position="right-start"
                style={{ maxWidth: "25em" }}
              />
            </span>
          )}
        </span>
      </Text>
    </>
  );

  return props.inlineTitle ? (
    <Group grow={true} justify="space-between" gap="24px">
      {Title}
      <RadioButtonGroup {...props} />
    </Group>
  ) : (
    <Stack gap="12px">
      {Title}
      <RadioButtonGroup {...props} />
    </Stack>
  );
};

export default RadioButtonGroup;
